import { inject, Injectable } from '@angular/core';
import { CrudService, UpdateOptions } from '@context/frontend/api-client';
import {
	ApplicationType,
	Chemical,
	ChemicalRevision,
	createChemical,
	createChemicalRevision,
	shouldCreateNewRevision,
} from '@context/shared/types/chemical';
import { sanitize } from '@context/shared/utils';
import {
	collection,
	CollectionReference,
	doc,
	DocumentReference,
	getDoc,
	getDocs,
	orderBy,
	query,
	QuerySnapshot,
	setDoc,
	Timestamp,
	updateDoc,
} from 'firebase/firestore';
import { BehaviorSubject } from 'rxjs';
import { ApplicationTypeService } from './application-type.service';

@Injectable({ providedIn: 'root' })
export class ChemicalService extends CrudService<Chemical> {
	static readonly CollectionId = 'chemicals';
	static readonly RevisionCollectionId = 'revisions';

	/**
	 * If a catalog chemical is currently being viewed, that chemical's id is stored here.
	 */
	readonly openChemicalId = new BehaviorSubject<string | null>(null);

	private readonly applicationTypeService = inject(ApplicationTypeService);

	constructor() {
		super(ChemicalService.CollectionId);
	}

	setOpenChemical(value: string | null) {
		const current = this.openChemicalId.value;
		if (current !== value) {
			this.openChemicalId.next(value);
		}
	}

	/**
	 * Creates a new chemical with its first revision.
	 * This is used when initially creating a chemical from the form.
	 */
	async createWithFirstRevision(
		chemical: Partial<Chemical>,
		revision: Partial<ChemicalRevision>,
	): Promise<{ chemical: Chemical; revision: ChemicalRevision }> {
		const chemicalPayload = createChemical(chemical);
		const createdChemical = await this.create(chemicalPayload);

		// Get the document reference using the id from the created chemical
		const chemicalRef = doc(
			this.collection,
			createdChemical.id!,
		) as DocumentReference<Chemical>;

		// Create the first revision, which will automatically update latestRevision
		const createdRevision = await this.createRevision(
			createdChemical,
			revision,
		);

		// Fetch the updated chemical to get the latestRevision reference
		const updatedChemical = (await getDoc(chemicalRef)).data() as Chemical;

		return {
			chemical: { ...updatedChemical, id: createdChemical.id! },
			revision: createdRevision,
		};
	}

	/**
	 * Creates a new chemical revision under a chemical document.
	 * Automatically updates the chemical's latestRevision reference.
	 */
	async createRevision(
		chemical: Partial<Chemical>,
		revision: Partial<ChemicalRevision>,
	): Promise<ChemicalRevision> {
		if (!chemical.id) throw new Error('Chemical ID is required');

		const now = Timestamp.now();
		const { revision: payload, revisionRef } =
			await this.writeRevisionDocument(chemical.id, revision, now);

		const chemicalRef = doc(
			this.collection,
			chemical.id,
		) as DocumentReference<Chemical>;

		// Update the parent chemical to reference this as the latest revision
		await updateDoc(chemicalRef, {
			latestRevision: revisionRef,
			updatedAt: now,
		});

		// Log activity for revision creation
		if (this.user.value?.id && this.userRef) {
			await this.activityService.create(
				revisionRef,
				{ type: 'create' },
				this.userRef,
			);
		}

		return payload;
	}

	/**
	 * Writes a revision subdocument without updating the parent chemical.
	 * Used when the caller batches the parent update into a single write.
	 */
	private async writeRevisionDocument(
		chemicalId: string,
		revision: Partial<ChemicalRevision>,
		now: Timestamp,
	): Promise<{
		revision: ChemicalRevision;
		revisionRef: DocumentReference<ChemicalRevision>;
	}> {
		const payload = createChemicalRevision(revision) as ChemicalRevision;

		payload.createdAt = now;
		payload.updatedAt = now;
		payload.deletedAt = null;
		payload.id = crypto.randomUUID();

		const chemicalRef = doc(
			this.collection,
			chemicalId,
		) as DocumentReference<Chemical>;

		const revisionCollection = collection(
			chemicalRef,
			ChemicalService.RevisionCollectionId,
		) as CollectionReference<ChemicalRevision, ChemicalRevision>;

		const revisionRef = doc(
			revisionCollection,
			payload.id,
		) as DocumentReference<ChemicalRevision>;

		await setDoc(revisionRef, sanitize(payload));

		return { revision: payload, revisionRef };
	}

	/**
	 * Gets all revisions for a chemical, ordered by createdAt descending
	 */
	getRevisionSnapshots(
		chemical: Partial<Chemical>,
	): Promise<QuerySnapshot<ChemicalRevision>> {
		const revisionCollection = collection(
			doc(this.collection, chemical.id!),
			ChemicalService.RevisionCollectionId,
		) as CollectionReference<ChemicalRevision, ChemicalRevision>;
		return getDocs(
			query(
				revisionCollection,
				orderBy('createdAt', 'desc'),
				orderBy('deletedAt', 'asc'),
			),
		);
	}

	/**
	 * Gets all revisions for a chemical, ordered by createdAt descending
	 */
	readonly getRevisions = (
		chemical: Partial<Chemical>,
	): Promise<ChemicalRevision[]> =>
		this.getRevisionSnapshots(chemical).then((snapshot) =>
			snapshot.docs.map((doc) => doc.data() as ChemicalRevision),
		);

	/**
	 * Fetches the latest revision for a chemical.
	 * If latestRevision reference exists, uses that; otherwise queries for the most recent.
	 */
	getLatestRevisionRef(
		chemical: Partial<Chemical>,
	): Promise<DocumentReference<ChemicalRevision>> {
		// If latestRevision reference exists on chemical, use it
		if (chemical.latestRevision) {
			return Promise.resolve(
				chemical.latestRevision as DocumentReference<ChemicalRevision>,
			);
		}

		// Fallback: query for the most recent non-deleted revision
		return this.getRevisionSnapshots(chemical).then((snapshot) => {
			const doc = snapshot.docs.find((doc) => !doc.data().deletedAt);
			if (!doc) throw new Error('No non-deleted revision found');
			return doc.ref as DocumentReference<ChemicalRevision>;
		});
	}

	/**
	 * Fetches the latest revision for a chemical.
	 * If latestRevision reference exists, uses that; otherwise queries for the most recent.
	 */
	readonly getLatestRevision = (
		chemical: Partial<Chemical>,
	): Promise<ChemicalRevision | null> =>
		getDoc(chemical.latestRevision as DocumentReference<ChemicalRevision>)
			.then((doc) => doc.data() as ChemicalRevision)
			.catch(() => null);

	/**
	 * Updates a specific revision's expiration date to the beginning of the current day.
	 * This effectively expires the revision immediately.
	 *
	 * @param revisionRef Reference to the revision document to update
	 * @returns The updated revision
	 */
	async expireRevision(
		revisionRef: DocumentReference<ChemicalRevision>,
	): Promise<ChemicalRevision> {
		const now = Timestamp.now();
		// Set expiration to the beginning of today (midnight)
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const expiresAt = Timestamp.fromDate(today);

		await updateDoc(revisionRef, { expiresAt, updatedAt: now });

		// Log activity for revision expiration
		if (this.user.value?.id && this.userRef) {
			await this.activityService.create(
				revisionRef,
				{ type: 'update' },
				this.userRef,
			);
		}

		return getDoc(revisionRef).then(
			(doc) => doc.data() as ChemicalRevision,
		);
	}

	/**
	 * Updates a chemical and optionally creates a new revision if the EPA Regulation Number changed.
	 * If only expiration date or notes change, the latest revision is updated directly.
	 * If the regulation number changes, a new revision is created.
	 *
	 * @param chemicalRef Reference to the chemical document
	 * @param payload Updated chemical data (name, activeIngredient)
	 * @param revisionData Updated revision data (regulationNumber, expiresAt, notes)
	 * @returns The updated chemical and revision data
	 */
	override async update(
		payload: Partial<Chemical> & { revision?: Partial<ChemicalRevision> },
		options: UpdateOptions,
	): Promise<Partial<Chemical>> {
		const { revision } = payload;
		if (!revision) throw new Error('Revision is required');
		if (!payload.id) throw new Error('Chemical ID is required');

		// remove the revision from the payload to avoid adding it to the document
		delete payload.revision;

		let latestRevision: ChemicalRevision | null = null;
		if (payload.latestRevision) {
			latestRevision = await getDoc(
				payload.latestRevision as DocumentReference<ChemicalRevision>,
			).then((doc) => doc.data() as ChemicalRevision);
			// remove in favor for a potential new revision
			delete payload.latestRevision;
		} else {
			latestRevision = await this.getLatestRevision(payload);
		}

		const now = Timestamp.now();
		const chemicalRef = doc(this.collection, payload.id);

		// Check if we need to create a new revision
		const isNewRevision = shouldCreateNewRevision(latestRevision, revision);
		if (isNewRevision && latestRevision) {
			const { revisionRef } = await this.writeRevisionDocument(
				payload.id,
				revision,
				now,
			);

			if (this.user.value?.id && this.userRef && options.addActivityLog) {
				await this.activityService.create(
					revisionRef,
					{ type: 'create' },
					this.userRef,
				);
			}

			await updateDoc(
				chemicalRef,
				sanitize({
					...payload,
					latestRevision: revisionRef,
					updatedAt: now,
				}),
			);
		} else if (latestRevision) {
			// Update the existing latest revision if only expiration or notes changed
			const revisionCollection = collection(
				doc(this.collection, payload.id),
				ChemicalService.RevisionCollectionId,
			) as CollectionReference<ChemicalRevision, ChemicalRevision>;

			const revisionRef = doc(
				revisionCollection,
				latestRevision.id,
			) as DocumentReference<ChemicalRevision>;

			await updateDoc(
				revisionRef,
				sanitize({ ...revision, updatedAt: now }),
			);

			// Log activity for revision update
			if (this.user.value?.id && this.userRef && options.addActivityLog) {
				await this.activityService.create(
					revisionRef,
					{ type: options.type },
					this.userRef,
				);
			}

			await updateDoc(
				chemicalRef,
				sanitize({ ...payload, updatedAt: now }),
			);
		} else {
			const { revisionRef } = await this.writeRevisionDocument(
				payload.id,
				revision,
				now,
			);

			if (this.user.value?.id && this.userRef && options.addActivityLog) {
				await this.activityService.create(
					revisionRef,
					{ type: 'create' },
					this.userRef,
				);
			}

			await updateDoc(
				chemicalRef,
				sanitize({
					...payload,
					latestRevision: revisionRef,
					updatedAt: now,
				}),
			);
		}

		if (this.user.value?.id && options.addActivityLog) {
			await this.logActivity({
				entity: chemicalRef,
				activity: { type: options.type },
				createdBy: this.userRef ?? this.user.value?.id,
			});
		}

		this.changed$.next();

		// Fetch the updated chemical
		const updatedChemical = (await getDoc(chemicalRef)).data() as Chemical;
		return { ...updatedChemical, id: chemicalRef.id };
	}

	/**
	 * Gets application types for a given revision.
	 * This is a convenience method that delegates to ApplicationTypeService.
	 */
	readonly getApplicationTypes = (
		revisionRef: DocumentReference<ChemicalRevision>,
	): Promise<ApplicationType[]> =>
		this.applicationTypeService.getApplicationTypes(revisionRef);

	/**
	 * Gets the revision reference for a chemical after create/update.
	 * Handles both new and existing revisions.
	 */
	private async getRevisionReference(
		chemical: Partial<Chemical>,
		wasNewRevision: boolean,
	): Promise<DocumentReference<ChemicalRevision>> {
		const chemicalRef = doc(
			this.collection,
			chemical.id!,
		) as DocumentReference<Chemical>;

		if (wasNewRevision || !chemical.latestRevision) {
			// Get the latest revision after update
			const updatedChemical = await getDoc(chemicalRef);
			const chemicalData = updatedChemical.data() as Chemical;
			if (!chemicalData?.latestRevision) {
				throw new Error('Failed to get revision reference');
			}
			return chemicalData.latestRevision as DocumentReference<ChemicalRevision>;
		}

		// Use existing latestRevision reference
		return chemical.latestRevision as DocumentReference<ChemicalRevision>;
	}

	/**
	 * Creates a new chemical with its first revision and application types.
	 * This is a convenience method that handles all the complexity of creating
	 * a chemical, revision, and saving application types.
	 */
	async createWithFirstRevisionAndApplicationTypes(
		chemical: Partial<Chemical>,
		revision: Partial<ChemicalRevision>,
		applicationTypes: Partial<ApplicationType>[],
	): Promise<{ chemical: Chemical; revision: ChemicalRevision }> {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');

		const result = await this.createWithFirstRevision(chemical, revision);
		const revisionRef = await this.getLatestRevisionRef(result.chemical);

		// Save application types
		const filteredApplicationTypes = (applicationTypes || []).filter(
			(at) => at.name && at.reiHours !== null,
		);

		if (filteredApplicationTypes.length) {
			await this.applicationTypeService.saveApplicationTypes(
				revisionRef,
				filteredApplicationTypes,
				organizationId,
				this.userRef ?? undefined,
			);
		}

		return result;
	}

	/**
	 * Updates a chemical and its revision, then saves application types.
	 * This is a convenience method that handles all the complexity of updating
	 * a chemical, potentially creating a new revision, and saving application types.
	 */
	async updateWithApplicationTypes(
		payload: Partial<Chemical> & { revision?: Partial<ChemicalRevision> },
		applicationTypes: Partial<ApplicationType>[],
		options: UpdateOptions,
	): Promise<Partial<Chemical>> {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');

		// Check if we'll create a new revision
		const { revision, id } = payload;
		if (!id) throw new Error('Chemical ID is required');
		if (!revision) throw new Error('Revision is required');

		// Get current chemical to check if we'll create a new revision
		const currentChemical = await getDoc(
			doc(this.collection, id) as DocumentReference<Chemical>,
		).then((doc) => doc.data() as Chemical);
		if (!currentChemical) throw new Error('Chemical not found');

		const latestRevision = await this.getLatestRevision(currentChemical);
		if (!latestRevision) throw new Error('Latest revision not found');

		const willCreateNewRevision = shouldCreateNewRevision(
			latestRevision,
			revision,
		);

		// Update the chemical (and potentially create new revision)
		const updatedChemical = await this.update(payload, options);

		// Get the revision reference after update
		const revisionRef = await this.getRevisionReference(
			updatedChemical,
			willCreateNewRevision,
		);

		// Save application types
		const filteredApplicationTypes = (applicationTypes || []).filter(
			(at) => at.name && at.reiHours !== null,
		);

		if (filteredApplicationTypes.length) {
			await this.applicationTypeService.saveApplicationTypes(
				revisionRef,
				filteredApplicationTypes,
				organizationId,
				this.userRef ?? undefined,
			);
		}

		return updatedChemical;
	}
}
